import dayjs from 'dayjs';

import {
  ClientKind,
  convertClientKind,
  convertReportFunction,
  HiveRequestAcceptanceStatus,
  RegistrationUf,
  ReportFunction,
  SlaStatus,
  Status,
  ufAbbreviation,
} from '../../../api/enumerations';
import { WorkOrderData } from '../../../api/workOrders/types';
import { FUNNEL_ORDER } from '../../../constants/status';
import { monthBucketsLast12 } from './dateRanges';

export const slaOnTimePct = (rows: WorkOrderData[]): number => {
  if (rows.length === 0) return 0;
  const overdue = rows.filter((r) => r.sla_status === SlaStatus.OVERDUE).length;
  return Math.round((1 - overdue / rows.length) * 100);
};

export const tmcDays = (rows: WorkOrderData[]): number => {
  const finished = rows.filter(
    (r) =>
      r.status === Status.ARCHIVED && typeof r.sla_days_elapsed === 'number'
  );
  if (finished.length === 0) return 0;
  const sum = finished.reduce((acc, r) => acc + (r.sla_days_elapsed ?? 0), 0);
  return Math.round(sum / finished.length);
};

export const rejectionPct = (rows: WorkOrderData[]): number => {
  if (rows.length === 0) return 0;
  const rejected = rows.filter(
    (r) =>
      r.hive_request?.acceptance_status === HiveRequestAcceptanceStatus.REJECTED
  ).length;
  return Math.round((rejected / rows.length) * 1000) / 10;
};

export const deltaPct = (current: number, previous: number): number | null => {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

interface StatusCount {
  status: Status;
  count: number;
}

export const groupByStatus = (rows: WorkOrderData[]): StatusCount[] => {
  const counts = new Map<Status, number>();
  FUNNEL_ORDER.forEach((s: Status) => counts.set(s, 0));
  rows.forEach((r) => counts.set(r.status, (counts.get(r.status) ?? 0) + 1));
  return FUNNEL_ORDER.map((status: Status) => ({
    status,
    count: counts.get(status) ?? 0,
  }));
};

interface CompanyStat {
  company: string;
  count: number;
  overduePct: number;
}

export const groupByCompany = (
  rows: WorkOrderData[],
  limit = 10
): CompanyStat[] => {
  const map = new Map<string, WorkOrderData[]>();
  rows.forEach((r) => {
    const key = r.worker_company?.name || 'sem empresa';
    const arr = map.get(key) ?? [];
    arr.push(r);
    map.set(key, arr);
  });
  const stats = Array.from(map.entries()).map(([company, list]) => {
    const overdue = list.filter(
      (r) => r.sla_status === SlaStatus.OVERDUE
    ).length;
    return {
      company,
      count: list.length,
      overduePct:
        list.length === 0 ? 0 : Math.round((overdue / list.length) * 100),
    };
  });
  stats.sort((a, b) => b.count - a.count);
  return stats.slice(0, limit);
};

interface RegionCount {
  uf: string;
  ufEnum: RegistrationUf;
  count: number;
}

export const groupByUf = (rows: WorkOrderData[], limit = 8): RegionCount[] => {
  const counts = new Map<RegistrationUf, number>();
  rows.forEach((r) => {
    if (!r.uf || (r.uf as number) === RegistrationUf.SELECT) return;
    counts.set(r.uf, (counts.get(r.uf) ?? 0) + 1);
  });
  const stats = Array.from(counts.entries()).map(([ufEnum, count]) => ({
    ufEnum,
    uf: ufAbbreviation[ufEnum] || '—',
    count,
  }));
  stats.sort((a, b) => b.count - a.count);
  return stats.slice(0, limit);
};

interface KindCount {
  label: string;
  count: number;
  kind: ClientKind;
}

export const groupByClientKind = (rows: WorkOrderData[]): KindCount[] => {
  const counts = new Map<ClientKind, number>();
  rows.forEach((r) => {
    counts.set(r.client_kind, (counts.get(r.client_kind) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([kind, count]) => ({
      kind,
      label: convertClientKind(kind) || 'desconhecido',
      count,
    }))
    .sort((a, b) => b.count - a.count);
};

interface FunctionCount {
  label: string;
  count: number;
  fn: ReportFunction;
}

export const groupByFunction = (
  rows: WorkOrderData[],
  limit = 6
): FunctionCount[] => {
  const counts = new Map<ReportFunction, number>();
  rows.forEach((r) => {
    counts.set(r.report_function, (counts.get(r.report_function) ?? 0) + 1);
  });
  const stats = Array.from(counts.entries()).map(([fn, count]) => ({
    fn,
    label: convertReportFunction(fn) || 'outros',
    count,
  }));
  stats.sort((a, b) => b.count - a.count);
  return stats.slice(0, limit);
};

interface MonthlyVolume {
  monthKey: string;
  monthLabel: string;
  created: number;
}

// The data model currently lacks a real `archived_at` field — only
// `limit_finish_at` (deadline) is available, which is a poor proxy for actual
// completion. Until the backend exposes a real archived_at, this series is
// limited to OS creation counts.
export const volumeByMonth = (rows: WorkOrderData[]): MonthlyVolume[] => {
  const buckets = monthBucketsLast12();
  const created = new Map<string, number>();
  buckets.forEach((b) => {
    created.set(b.key, 0);
  });
  rows.forEach((r) => {
    if (r.created_at) {
      const key = dayjs(r.created_at).format('YYYY-MM');
      if (created.has(key)) created.set(key, (created.get(key) ?? 0) + 1);
    }
  });
  return buckets.map((b) => ({
    monthKey: b.key,
    monthLabel: b.label,
    created: created.get(b.key) ?? 0,
  }));
};

export const criticalOrders = (
  rows: WorkOrderData[],
  n = 5
): WorkOrderData[] => {
  const overdue = rows.filter((r) => r.sla_status === SlaStatus.OVERDUE);
  overdue.sort(
    (a, b) => (a.sla_days_remaining ?? 0) - (b.sla_days_remaining ?? 0)
  );
  return overdue.slice(0, n);
};
